import { useState, useEffect } from 'react';
import { User } from '../types';
import { authAPI, teamAPI } from '../utils/api';
import fullTimeAPI from '../utils/fullTimeAPI';

/** Map HRMS `profile_picture` onto onboarding `avatar` for layout/profile UI */
function normalizePortalUser(raw: User | Record<string, unknown> | null): User | null {
  if (!raw) return null;
  const r = raw as Record<string, unknown>;
  const avatar = (r.avatar || r.profile_picture) as string | undefined;
  return { ...(raw as User), ...(avatar ? { avatar } : {}) };
}

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    setIsLoading(true);
    
    try {
      // First check if user is authenticated via backend cookies
      const authResponse = await authAPI.checkAuth();
      
      if (authResponse.success && authResponse.user) {
        let user = normalizePortalUser(authResponse.user) as User;
        
        // If user is not HR/Admin, check their team role (do not override admin identity)
        const roleLower = String(user.role || '').toLowerCase();
        if (roleLower !== 'hr' && roleLower !== 'admin') {
          console.log('🔍 Non-HR/Admin user detected, checking team role via /api/team/teams/teamrole...');
          console.log('📧 User email:', user.email);
          
          // Add a small delay to ensure cookies are properly set
          await new Promise(resolve => setTimeout(resolve, 100));
          
          try {
            const teamRoleResponse = await teamAPI.getMyTeamRole();
            console.log('📡 Team role API response:', teamRoleResponse);
            
            if (teamRoleResponse.success && teamRoleResponse.data && teamRoleResponse.data.length > 0) {
              // Get the first team role (user might be in multiple teams)
              const teamData = teamRoleResponse.data[0];
              const teamRole = teamData.team_role;
              const teamId = teamData.team_id;
              const teamName = teamData.team_name;
              console.log('✅ Team role found:', teamRole);
              console.log('🏢 Team details:', teamData);
              
              // Override the role based on team_role and add team info
              if (teamRole === 'BUDDY') {
                user.role = 'buddy';
                user.team_role = teamRole;
                user.team_id = teamId;
                user.team_name = teamName;
                console.log('🤝 User role updated to BUDDY - will show Buddy Dashboard');
              } else if (teamRole === 'JOINER') {
                user.role = 'joiner';
                user.team_role = teamRole;
                user.team_id = teamId;
                user.team_name = teamName;
                console.log('👋 User role updated to JOINER - will show Joiner Dashboard');
              } else if (teamRole === 'TEAM_LEAD' || teamRole === 'MEMBER') {
                user.team_role = teamRole;
                user.team_id = teamId;
                user.team_name = teamName;
                console.log(`👔 Team role is ${teamRole} - will show appropriate dashboard`);
              } else {
                console.log('❓ Unknown team role:', teamRole);
              }
              
              // Active full-time track for MEMBER performance nav
              if (teamRole === 'MEMBER') {
                try {
                  const trackInfo = await fullTimeAPI.getMe();
                  user.hasFullTimeTrack = Boolean(trackInfo.hasActiveTrack);
                } catch (trackErr) {
                  console.warn('Could not load full-time track:', trackErr);
                  user.hasFullTimeTrack = false;
                }
              }

              // Update localStorage with the updated user info
              localStorage.setItem('current_user', JSON.stringify(normalizePortalUser(user)));
              console.log('💾 Updated localStorage with new role information');
            } else {
              console.log('❌ No team role found for user - using default role');
            }
          } catch (teamError) {
            console.error('🚨 Failed to get team role:', teamError);
          }
        } else {
          console.log('👑 HR user detected - will show HR Dashboard');
        }
        
        // Log final user state
        console.log('🎯 Final user state:', {
          name: user.name,
          email: user.email,
          role: user.role,
          team_role: user.team_role,
          team_id: user.team_id,
          team_name: user.team_name
        });
        
        // User is authenticated via backend
        setCurrentUser(normalizePortalUser(user));
        setIsAuthenticated(true);
        console.log('User authenticated via backend:', user);
      } else {
        // No valid backend authentication; clear any stale local state.
        localStorage.removeItem('auth_token');
        localStorage.removeItem('current_user');
        localStorage.removeItem('token');
        setCurrentUser(null);
        setIsAuthenticated(false);
        console.log('No valid backend authentication found');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Treat auth-check failures as unauthenticated for security.
      localStorage.removeItem('auth_token');
      localStorage.removeItem('current_user');
      localStorage.removeItem('token');
      setCurrentUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await authAPI.login(email, password);
      if (response.success && response.data?.user) {
        setCurrentUser(normalizePortalUser(response.data.user));
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      console.log('🔄 Starting logout process...');
      
      // Call backend logout (this will clear cookies on server side)
      await authAPI.logout();
      
      // Clear local state
      
      setCurrentUser(null);
      setIsAuthenticated(false);
      
      console.log('✅ Logout completed successfully');
    } catch (error) {
      console.error('❌ Logout failed:', error);
      // Still clear local data even if API fails
      setCurrentUser(null);
      setIsAuthenticated(false);
      
      // If backend logout fails, try to clear cookies manually as fallback
      document.cookie = "accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      console.log('🧹 Manual cookie cleanup performed');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAuth = async (): Promise<void> => {
    await checkAuthStatus();
  };

  return {
    currentUser,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshAuth
  };
};
